import { useCallback, useEffect, useState } from 'react';
import { Box, Divider, List, ListItemButton, ListItemText, Paper, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import {
  getClubSections,
  getSections,
  portalErrorMessage
} from '../../features/portal/portalService';
import {
  clubRinkQrPrintPath,
  clubSectionPath,
  clubSectionQrPrintPath,
  secretarySectionPath
} from './portalPaths';
import {
  PortalCounts,
  PortalEmpty,
  PortalError,
  PortalLoading,
  PortalPageHeader,
  PortalPrintActions
} from './portalCommon';
import PortalDrawExport from './portalDrawExport';

const PortalSections = ({ mode }) => {
  const { eventId, venueId } = useParams();
  const { token } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);

  const load = useCallback(() => {
    if (!token || !eventId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    const request =
      mode === 'secretary' ? getClubSections(token, venueId) : getSections(token);

    request
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((requestError) => {
        if (!cancelled) setError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, mode, venueId, eventId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load, retry]);

  const title =
    mode === 'secretary'
      ? data?.club?.venueName || 'Club'
      : data?.event?.name || 'Sections';

  return (
    <Box>
      <PortalPageHeader
        title={mode === 'secretary' ? title : 'Sections'}
        subtitle={
          mode === 'secretary'
            ? 'Choose a section'
            : data?.event?.name || 'Choose a section'
        }
        backTo={mode === 'secretary' ? `/portal/${encodeURIComponent(eventId)}/secretary` : null}
        backLabel="Clubs"
        action={
          mode === 'club' ? (
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              justifyContent="flex-end">
              <PortalPrintActions
                rinkTo={clubRinkQrPrintPath(eventId, venueId)}
                sectionTo={clubSectionQrPrintPath(eventId, venueId)}
              />
              <PortalDrawExport mode={mode} />
            </Stack>
          ) : (
            <PortalDrawExport mode={mode} />
          )
        }
      />

      {loading && <PortalLoading />}
      {!loading && error && <PortalError message={error} onRetry={() => setRetry((n) => n + 1)} />}

      {!loading && !error && data && data.sections.length === 0 && (
        <PortalEmpty>This club has no games in any section yet.</PortalEmpty>
      )}

      {!loading && !error && data && data.sections.length > 0 && (
        <Paper elevation={2}>
          <List disablePadding>
            {data.sections.map((section, index) => (
              <Box key={section.id}>
                {index > 0 && <Divider />}
                <ListItemButton
                  component={RouterLink}
                  to={
                    mode === 'secretary'
                      ? secretarySectionPath(eventId, venueId, section.id)
                      : clubSectionPath(eventId, venueId, section.id)
                  }
                  sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={section.name}
                    secondary={<PortalCounts {...section} />}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ChevronRightIcon color="action" />
                </ListItemButton>
              </Box>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default PortalSections;
